/**
 * GameInvitePopup - Popup notification for game invites
 * 
 * Displays when a user is added to a game by another player.
 * Shows game details, player list with confirmation status, and actions.
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { handicapLabel } from '../utils/handicap';
import { toast } from 'sonner';
import { X, MessageCircle, User, ChevronDown, ChevronUp } from 'lucide-react';
import { Button } from './ui/button';
import UserBubble from './UserBubble';

const API = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : '/api';

/**
 * Mini Profile Card - Shows when tapping a player
 */
const MiniProfileCard = ({ player, onClose, onMessage, onViewProfile }) => {
  if (!player) return null;
  
  return (
    <div 
      className="fixed inset-0 bg-black/70 flex items-center justify-center z-[1100] p-4"
      onClick={onClose}
    >
      <div 
        className="bg-[#1a1a1a] border-2 border-green-500 rounded-2xl w-full max-w-[300px] p-6 relative"
        style={{
          boxShadow: '0 0 30px rgba(34, 197, 94, 0.4), 0 20px 40px rgba(0, 0, 0, 0.5)'
        }}
        onClick={e => e.stopPropagation()}
      >
        <button 
          onClick={onClose}
          className="absolute top-3 right-3 w-7 h-7 rounded-full bg-[#333] flex items-center justify-center text-gray-400 hover:text-white hover:bg-[#444] transition-colors"
        >
          <X className="w-4 h-4" />
        </button>
        
        {/* Header */}
        <div className="text-center mb-5">
          <div className="relative w-20 h-20 mx-auto mb-3">
            {player.picture ? (
              <img 
                src={player.picture} 
                alt={player.name}
                className="w-full h-full rounded-full object-cover border-3 border-green-500"
                style={{ boxShadow: '0 0 20px rgba(34, 197, 94, 0.4)' }}
              />
            ) : (
              <div 
                className="w-full h-full rounded-full bg-gradient-to-br from-green-500 to-green-600 flex items-center justify-center text-white text-2xl font-bold border-3 border-green-400"
                style={{ boxShadow: '0 0 20px rgba(34, 197, 94, 0.4)' }}
              >
                {player.name?.charAt(0)?.toUpperCase() || '?'}
              </div>
            )}
            {/* Online indicator */}
            <div className="absolute bottom-1 right-1 w-4 h-4 bg-green-500 rounded-full border-2 border-[#1a1a1a] animate-pulse" />
          </div>
          <div className="text-xl font-bold text-white">{player.name}</div>
          {player.handle && (
            <div className="text-sm text-green-400">@{player.handle}</div>
          )}
        </div>
        
        {/* Stats */}
        <div className="flex justify-around py-4 border-t border-b border-[#2a2a2a] mb-4">
          <div className="text-center">
            <div data-testid={`invite-player-handicap-${player.player_id || player.user_id}`} className="text-sm font-bold text-white">{handicapLabel(player.handicap)}</div>
            <div className="text-xs text-gray-500 uppercase">Handicap</div>
          </div>
          <div className="text-center">
            <div className="text-lg font-bold text-white">{player.rounds_played || '-'}</div>
            <div className="text-xs text-gray-500 uppercase">Rounds</div>
          </div>
          <div className="text-center">
            <div className="text-lg font-bold text-white">{player.buddies_count || '-'}</div>
            <div className="text-xs text-gray-500 uppercase">Buddies</div>
          </div>
        </div>
        
        {/* Actions */}
        <div className="flex gap-3 mb-3">
          <Button
            onClick={() => onMessage(player)}
            className="flex-1 bg-gradient-to-r from-green-500 to-green-600 hover:from-green-400 hover:to-green-500 text-white"
            style={{ boxShadow: '0 4px 15px rgba(34, 197, 94, 0.3)' }}
          >
            <MessageCircle className="w-4 h-4 mr-2" />
            Message
          </Button>
          <Button
            onClick={() => onViewProfile(player)}
            variant="outline"
            className="flex-1 border-[#333] text-gray-300 hover:bg-[#333] hover:text-white"
          >
            <User className="w-4 h-4 mr-2" />
            Profile
          </Button>
        </div>
        
        {player.is_buddy && (
          <div className="text-center text-sm text-green-400 flex items-center justify-center gap-2">
            <span className="w-5 h-5 bg-green-500 rounded-full flex items-center justify-center text-black text-xs font-bold">✓</span>
            You're buddies
          </div>
        )}
      </div>
    </div>
  );
};

/**
 * Player Bubble with confirmation status
 */
const PlayerBubbleWithStatus = ({ player, isYou, onClick }) => {
  const initials = player.name?.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase() || '?';
  
  return (
    <div 
      className={`flex flex-col items-center gap-1 ${!isYou && player.user_id ? 'cursor-pointer' : ''}`}
      onClick={() => !isYou && player.user_id && onClick?.(player)}
    >
      <div className="relative">
        <div 
          className={`w-11 h-11 rounded-full flex items-center justify-center text-sm font-bold text-white border-2 transition-transform ${
            !isYou && player.user_id ? 'hover:scale-110' : ''
          } ${
            isYou 
              ? 'bg-gradient-to-br from-green-500 to-green-600 border-green-500' 
              : player.is_confirmed 
                ? 'bg-gradient-to-br from-blue-500 to-blue-600 border-[#333]'
                : 'bg-gradient-to-br from-gray-600 to-gray-700 border-[#555] opacity-70'
          }`}
          style={isYou ? { boxShadow: '0 0 12px rgba(34, 197, 94, 0.5)' } : {}}
        >
          {player.picture ? (
            <img src={player.picture} alt={player.name} className="w-full h-full rounded-full object-cover" />
          ) : (
            initials
          )}
        </div>
        
        {/* Status badge */}
        {player.is_confirmed ? (
          <div 
            className="absolute -bottom-0.5 -right-0.5 w-4 h-4 bg-green-500 rounded-full flex items-center justify-center text-[9px] text-black font-bold border-2 border-[#1a1a1a]"
            style={{ boxShadow: '0 0 8px rgba(34, 197, 94, 0.6)' }}
          >
            ✓
          </div>
        ) : (
          <div className="absolute -bottom-0.5 -right-0.5 w-4 h-4 bg-gray-600 rounded-full flex items-center justify-center text-[9px] text-gray-300 font-bold border-2 border-[#1a1a1a]">
            ?
          </div>
        )}
      </div>
      <span className="text-[11px] text-gray-400">{isYou ? 'You' : player.name?.split(' ')[0]}</span>
    </div>
  );
};

/**
 * Main Game Invite Popup Component
 */
const GameInvitePopup = ({ invite, currentUserId, onJoin, onDismiss, onClose }) => {
  const navigate = useNavigate();
  const [showAllPlayers, setShowAllPlayers] = useState(false);
  const [selectedPlayer, setSelectedPlayer] = useState(null);
  const [isJoining, setIsJoining] = useState(false);
  
  if (!invite) return null;
  
  const players = invite.enriched_players || [];
  const displayPlayers = players.slice(0, 3);
  const remainingCount = Math.max(0, players.length - 3);
  
  // Determine game type display
  const getGameTypeDisplay = () => {
    const type = invite.game_type || 'group';
    switch (type.toLowerCase()) {
      case 'skins': return 'SKINS GAME';
      case 'match_play': return 'MATCH PLAY';
      case 'stroke_play': return 'STROKE PLAY';
      default: return 'GROUP ROUND';
    }
  };
  
  // Get wager display
  const getWagerDisplay = () => {
    if (invite.entry_fee > 0) {
      if (invite.game_type === 'skins') {
        return `$${invite.entry_fee} per hole`;
      }
      return `$${invite.entry_fee} entry`;
    }
    return 'Friendly round';
  };
  
  const handleJoin = async () => {
    setIsJoining(true);
    try {
      await axios.post(`${API}/group-rounds/${invite.round_id}/confirm`);
      toast.success('You\'ve joined the round!');
      onJoin?.(invite);
      // Navigate to the round
      navigate(`/group-round/${invite.round_id}`, {
        state: {
          roundData: invite,
          scorecardType: invite.scorecard_type || 'quickcard'
        }
      });
    } catch (error) {
      console.error('Error joining round:', error);
      toast.error('Failed to join round');
    } finally {
      setIsJoining(false);
    }
  };
  
  const handleDismiss = async () => {
    try {
      await axios.post(`${API}/group-rounds/${invite.round_id}/dismiss`);
      onDismiss?.(invite);
    } catch (error) {
      console.error('Error dismissing invite:', error);
    }
  };
  
  const handleMessagePlayer = (player) => {
    setSelectedPlayer(null);
    navigate(`/chat/${player.user_id}`);
  };
  
  const handleViewProfile = (player) => {
    setSelectedPlayer(null);
    navigate(`/profile/${player.user_id}`);
  };
  
  return (
    <>
      {/* Overlay */}
      <div className="fixed inset-0 bg-black/85 flex items-center justify-center z-[1000] p-5">
        {/* Popup Card */}
        <div 
          className="bg-[#1a1a1a] border-2 border-green-500 rounded-3xl w-full max-w-[360px] overflow-hidden"
          style={{
            boxShadow: '0 0 20px rgba(34, 197, 94, 0.6), 0 0 40px rgba(34, 197, 94, 0.4), 0 0 60px rgba(34, 197, 94, 0.2), 0 25px 50px rgba(0, 0, 0, 0.5)',
            animation: 'borderGlow 2.5s ease-in-out infinite alternate'
          }}
        >
          {/* Header */}
          <div className="p-5 pb-4 text-center border-b border-[#2a2a2a] bg-gradient-to-b from-[#1f1f1f] to-[#1a1a1a]">
            {/* Logo */}
            <div className="flex items-center justify-center gap-2.5 mb-4">
              <img 
                src="/images/par-seeker-logo.png" 
                alt="Par Seeker" 
                className="w-11 h-auto"
                style={{ filter: 'drop-shadow(0 0 8px rgba(212, 165, 52, 0.4))' }}
              />
              <div className="text-2xl font-extrabold tracking-wide">
                <span className="text-[#d4a534]" style={{ textShadow: '0 0 20px rgba(212, 165, 52, 0.5)' }}>PAR</span>
                {' '}
                <span className="text-green-500" style={{ textShadow: '0 0 20px rgba(34, 197, 94, 0.5)' }}>SEEKER</span>
              </div>
            </div>
            
            {/* Invitational */}
            <div 
              className="text-sm font-bold tracking-[4px] text-green-500 uppercase mb-2"
              style={{ 
                textShadow: '0 0 10px rgba(34, 197, 94, 0.8), 0 0 20px rgba(34, 197, 94, 0.5), 0 0 30px rgba(34, 197, 94, 0.3)',
                animation: 'textPulse 2s ease-in-out infinite'
              }}
            >
              INVITATIONAL
            </div>
            
            {/* Course Name */}
            <div className="text-xl font-bold text-white" style={{ textShadow: '0 2px 10px rgba(0, 0, 0, 0.5)' }}>
              {invite.course_name}
            </div>
          </div>
          
          {/* Content */}
          <div className="p-5">
            {/* Game Info */}
            <div className="text-center mb-5">
              <div 
                className="inline-block text-sm font-bold text-green-500 uppercase tracking-[2px] px-5 py-2 bg-green-500/10 border border-green-500/30 rounded-full mb-3"
                style={{ textShadow: '0 0 10px rgba(34, 197, 94, 0.5)' }}
              >
                {getGameTypeDisplay()}
              </div>
              <div 
                className="text-xl font-bold text-amber-400"
                style={{ textShadow: '0 0 15px rgba(251, 191, 36, 0.4)' }}
              >
                {getWagerDisplay()}
              </div>
            </div>
            
            {/* Players */}
            <div className="bg-[#0f0f0f] border border-[#2a2a2a] rounded-2xl p-4">
              <div className="flex items-center justify-center gap-3 flex-wrap">
                {displayPlayers.map((player) => (
                  <PlayerBubbleWithStatus
                    key={player.user_id}
                    player={player}
                    isYou={player.user_id === currentUserId}
                    onClick={setSelectedPlayer}
                  />
                ))}
                
                {remainingCount > 0 && (
                  <button
                    onClick={() => setShowAllPlayers(!showAllPlayers)}
                    className="w-11 h-11 rounded-full bg-[#1a1a1a] border-2 border-dashed border-[#444] text-gray-400 text-xs font-semibold hover:border-green-500 hover:text-green-500 hover:bg-green-500/10 transition-all"
                  >
                    +{remainingCount}
                  </button>
                )}
              </div>
              
              {/* Expanded Players List */}
              {showAllPlayers && (
                <div className="mt-4 pt-4 border-t border-[#2a2a2a]">
                  <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3 flex items-center justify-between">
                    <span>All Players ({players.length})</span>
                    <button onClick={() => setShowAllPlayers(false)}>
                      <ChevronUp className="w-4 h-4" />
                    </button>
                  </div>
                  <div className="space-y-2 max-h-[200px] overflow-y-auto">
                    {players.map((player) => (
                      <div 
                        key={player.user_id}
                        className={`flex items-center gap-3 p-2 rounded-lg transition-colors ${
                          player.user_id !== currentUserId && player.user_id ? 'hover:bg-[#1a1a1a] cursor-pointer' : ''
                        }`}
                        onClick={() => player.user_id !== currentUserId && player.user_id && setSelectedPlayer(player)}
                      >
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold text-white ${
                          player.user_id === currentUserId
                            ? 'bg-gradient-to-br from-green-500 to-green-600'
                            : player.is_host
                              ? 'bg-gradient-to-br from-green-500 to-green-600'
                              : player.is_confirmed
                                ? 'bg-gradient-to-br from-blue-500 to-blue-600'
                                : 'bg-gradient-to-br from-gray-600 to-gray-700 opacity-70'
                        }`}>
                          {player.picture ? (
                            <img src={player.picture} alt={player.name} className="w-full h-full rounded-full object-cover" />
                          ) : (
                            player.name?.charAt(0)?.toUpperCase() || '?'
                          )}
                        </div>
                        <span className="flex-1 text-sm text-gray-300">
                          {player.user_id === currentUserId ? 'You' : player.name}
                        </span>
                        {player.is_host ? (
                          <span className="text-xs text-green-400 bg-green-500/15 px-2 py-0.5 rounded-full">Host</span>
                        ) : player.is_confirmed ? (
                          <span className="text-xs text-green-400 bg-green-500/15 px-2 py-0.5 rounded-full">✓ Confirmed</span>
                        ) : (
                          <span className="text-xs text-gray-500 bg-gray-500/15 px-2 py-0.5 rounded-full">Pending</span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
          
          {/* Buttons */}
          <div className="flex gap-3 p-5 pt-0">
            <Button
              onClick={handleDismiss}
              variant="outline"
              className="flex-1 border-2 border-[#444] text-gray-400 hover:border-[#666] hover:text-gray-200 py-6"
            >
              DISMISS
            </Button>
            <Button
              onClick={handleJoin}
              disabled={isJoining}
              className="flex-1 bg-gradient-to-r from-green-500 to-green-600 hover:from-green-400 hover:to-green-500 text-white py-6"
              style={{ 
                boxShadow: '0 4px 15px rgba(34, 197, 94, 0.4), 0 0 20px rgba(34, 197, 94, 0.2)',
                animation: 'joinPulse 2s ease-in-out infinite'
              }}
            >
              {isJoining ? 'JOINING...' : 'JOIN ROUND'}
            </Button>
          </div>
          
          <div className="text-center pb-4 text-xs text-gray-600">
            Dismiss only closes this popup. You're still in the game.
          </div>
        </div>
      </div>
      
      {/* Mini Profile Card */}
      {selectedPlayer && (
        <MiniProfileCard
          player={selectedPlayer}
          onClose={() => setSelectedPlayer(null)}
          onMessage={handleMessagePlayer}
          onViewProfile={handleViewProfile}
        />
      )}
      
      {/* CSS Animations */}
      <style>{`
        @keyframes borderGlow {
          0% { 
            box-shadow: 0 0 15px rgba(34, 197, 94, 0.5), 0 0 30px rgba(34, 197, 94, 0.3), 0 0 45px rgba(34, 197, 94, 0.15), 0 25px 50px rgba(0, 0, 0, 0.5);
            border-color: rgba(34, 197, 94, 0.7);
          }
          100% { 
            box-shadow: 0 0 20px rgba(34, 197, 94, 0.7), 0 0 40px rgba(34, 197, 94, 0.5), 0 0 60px rgba(34, 197, 94, 0.3), 0 25px 50px rgba(0, 0, 0, 0.5);
            border-color: rgba(34, 197, 94, 1);
          }
        }
        
        @keyframes textPulse {
          0%, 100% { 
            text-shadow: 0 0 10px rgba(34, 197, 94, 0.8), 0 0 20px rgba(34, 197, 94, 0.5), 0 0 30px rgba(34, 197, 94, 0.3);
          }
          50% { 
            text-shadow: 0 0 15px rgba(34, 197, 94, 1), 0 0 30px rgba(34, 197, 94, 0.7), 0 0 45px rgba(34, 197, 94, 0.4);
          }
        }
        
        @keyframes joinPulse {
          0%, 100% { 
            box-shadow: 0 4px 15px rgba(34, 197, 94, 0.4), 0 0 20px rgba(34, 197, 94, 0.2);
          }
          50% { 
            box-shadow: 0 4px 20px rgba(34, 197, 94, 0.6), 0 0 30px rgba(34, 197, 94, 0.4);
          }
        }
      `}</style>
    </>
  );
};

export default GameInvitePopup;
